import React, { useContext, useEffect } from 'react'
import { useLazyQuery, gql, NetworkStatus } from '@apollo/client'
import { Paper, Typography } from '@material-ui/core'
import ChartColumn from '../../../../components/charts/ChartColumn'
import ChartColumnSkeleton from '../../../../components/skeletons/ChartColumnSkeleton'
import { ChartColumnThemes } from '../../../../components/charts/types/ChartColumn'
import { DailyMonitoringFiltersContext } from '../../../../context/DailyMonitoringFiltersContext'
import { FilterDailyMonitoring } from '../../../../types/FilterDailyMonitoring'
import { formatDateCard, formatCurrency, formatNumber } from '../../../../utils/Helpers'
import { STATUS_DELIVERED } from '../../../../types/Order'
import { defaultDateRange, defaultFormatTimeZone } from '../../../../utils/consts'

const GET_DATA = gql`
query OrdersAggs($filters: JSON,) {
  ordersAggs(
    filters: $filters,
    size: 0,
    query: {
      bool: {
        filter: [
          {
            term: {
              status_translate_keyword: "${STATUS_DELIVERED}"
            }
          }
        ]
      }
    },
    aggs: {
      values: {
        terms: {
          script: {
            lang: "painless",
            source: "doc['created_at'].value.getHour()"
          },
          size: 24
        },
        aggs: {
          receita: {
            stats: {
              field: "total"
            }
          }
        }
      }
    }
    ) {
      aggregations
    }
  }
`
interface Bucket {
  key: string
  doc_count: number,
  receita: {
    sum: number,
    avg: number,
  }
}
const PharmaciesTopSales = (): JSX.Element => {
  const { filters } = useContext(DailyMonitoringFiltersContext)
  const [getData, { loading, error, data, networkStatus }] = useLazyQuery(
    GET_DATA, {
      notifyOnNetworkStatusChange: true
    }
  )

  useEffect(() => {
    let createdAt: FilterDailyMonitoring['dateRange'] = { ...defaultDateRange }

    if (filters.dateRange) {
      createdAt = {
        ...filters.dateRange,
        ...defaultFormatTimeZone
      }
    }
    getData({
      variables: { filters: { ...filters, createdAt } }
    })
  }, [filters])
  if (networkStatus === NetworkStatus.refetch) return <ChartColumnSkeleton />
  if (loading || !data) return <ChartColumnSkeleton />
  if (error) return <p>Error :(</p>
  if (!data.ordersAggs.aggregations) return <p>No data found</p>

  const buckets:Array<Bucket> = new Array<Bucket>()
  data.ordersAggs.aggregations.values.buckets.forEach((bucket: Bucket) => buckets.push(bucket))
  const categories: Array<string> = []
  const seriesDataAVG: Array<number> = []
  const seriesDataSUM: Array<number> = []
  buckets.sort((a:Bucket, b:Bucket) => parseFloat(a.key) - parseFloat(b.key))
  buckets.forEach((bucket: Bucket) => {
    categories.push(bucket.key)
    seriesDataAVG.push(bucket.receita.avg)
    seriesDataSUM.push(bucket.receita.sum)
  })
  const options = {
    chart: {
      id: 'gmv-day'
    },
    dataLabels: {
      formatter: (val: number) => {
        return formatNumber(val, 0, true)
      },
      style: {
        colors: ['#304758']
      }
    },
    xaxis: {
      categories,
      labels: {
        formatter: (value: number) => {
          return `${value}h`
        }
      }
    },
    yaxis: {
      seriesDataSUM,
      labels: {
        formatter: (value: number) => {
          return formatCurrency(value)
        }
      }
    },
    grid: {
      show: false
    }
  }
  const series = [
    {
      name: 'Total',
      data: seriesDataSUM
    }, {
      name: 'Ticket médio',
      data: seriesDataAVG
    }
  ]

  if (!data) return <></>

  return <Paper elevation={2}>
    <Typography paragraph style={{ paddingLeft: '20px', paddingTop: '20px' }}>GMV do dia ({formatDateCard(filters.dateRange?.initialDate, filters.dateRange?.finalDate)})</Typography>
    <ChartColumn options={options} series={series} theme={ChartColumnThemes.threecolors} height={280} />
  </Paper>
}

export default PharmaciesTopSales
